function prefersReduce() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function bootReveal(root) {
  const nodes = [...root.querySelectorAll('[data-reveal]')];
  if (!nodes.length) return () => {};
  if (prefersReduce()) {
    nodes.forEach((node) => node.classList.add('is-in'));
    return () => {};
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
  );
  nodes.forEach((node) => io.observe(node));
  return () => io.disconnect();
}

function bootCarousel(root) {
  const scroller = root.querySelector('[data-cols-track]');
  const prev = root.querySelector('[data-cols-prev]');
  const next = root.querySelector('[data-cols-next]');
  const dotsWrap = root.querySelector('[data-cols-dots]');
  const cards = [...(scroller?.querySelectorAll('[data-cols-card]') || [])];
  if (!scroller || cards.length < 2) {
    prev?.setAttribute('hidden', '');
    next?.setAttribute('hidden', '');
    dotsWrap?.setAttribute('hidden', '');
    return () => {};
  }

  let rafId = 0;
  let pageCountCache = 0;

  const gapOf = () => {
    const styles = window.getComputedStyle(scroller);
    return parseFloat(styles.columnGap || styles.gap) || 0;
  };

  const step = () => {
    const width = cards[0].getBoundingClientRect().width;
    return Math.max(1, width + gapOf());
  };

  const maxScroll = () => Math.max(0, scroller.scrollWidth - scroller.clientWidth);

  const pageCount = () => {
    const max = maxScroll();
    if (max <= 2) return 1;
    return Math.max(1, Math.round(max / step()) + 1);
  };

  const pageIndex = () => {
    const s = step();
    return Math.min(pageCount() - 1, Math.max(0, Math.round(scroller.scrollLeft / s)));
  };

  const scrollToPage = (index) => {
    const pages = pageCount();
    const target = Math.min(pages - 1, Math.max(0, index));
    scroller.scrollTo({
      left: target * step(),
      behavior: prefersReduce() ? 'auto' : 'smooth',
    });
  };

  const go = (dir) => {
    scrollToPage(pageIndex() + dir);
  };

  const paint = () => {
    const max = maxScroll();
    const atStart = scroller.scrollLeft <= 4;
    const atEnd = scroller.scrollLeft >= max - 4;
    const pages = pageCount();
    const hideNav = pages <= 1 || max <= 2;

    prev?.toggleAttribute('hidden', hideNav);
    next?.toggleAttribute('hidden', hideNav);
    dotsWrap?.toggleAttribute('hidden', hideNav);

    if (prev) prev.disabled = atStart;
    if (next) next.disabled = atEnd;
    if (!dotsWrap) return;

    const current = Math.min(pages - 1, Math.max(0, pageIndex()));
    if (dotsWrap.childElementCount !== pages || pageCountCache !== pages) {
      pageCountCache = pages;
      dotsWrap.innerHTML = '';
      for (let i = 0; i < pages; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'artink-cols__dot';
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.addEventListener('click', () => scrollToPage(i));
        dotsWrap.append(dot);
      }
    }

    [...dotsWrap.children].forEach((dot, i) => {
      dot.classList.toggle('is-on', i === current);
      dot.setAttribute('aria-current', i === current ? 'true' : 'false');
    });
  };

  const schedulePaint = () => {
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(paint);
  };

  const onPrev = () => go(-1);
  const onNext = () => go(1);
  prev?.addEventListener('click', onPrev);
  next?.addEventListener('click', onNext);
  scroller.addEventListener('scroll', schedulePaint, { passive: true });

  const ro = new ResizeObserver(() => {
    // Keep position sensible when column count / card width changes
    const pages = pageCount();
    const current = Math.min(pages - 1, Math.max(0, pageIndex()));
    scroller.scrollLeft = current * step();
    schedulePaint();
  });
  ro.observe(scroller);
  cards.forEach((card) => ro.observe(card));

  const mqTablet = window.matchMedia('(max-width: 989px)');
  const mqMobile = window.matchMedia('(max-width: 640px)');
  const onBreakpoint = () => {
    scroller.scrollLeft = 0;
    schedulePaint();
  };
  mqTablet.addEventListener?.('change', onBreakpoint);
  mqMobile.addEventListener?.('change', onBreakpoint);

  paint();

  return () => {
    cancelAnimationFrame(rafId);
    prev?.removeEventListener('click', onPrev);
    next?.removeEventListener('click', onNext);
    scroller.removeEventListener('scroll', schedulePaint);
    mqTablet.removeEventListener?.('change', onBreakpoint);
    mqMobile.removeEventListener?.('change', onBreakpoint);
    ro.disconnect();
  };
}

function bootCollections(root) {
  if (!root || root.dataset.booted === '1') return;
  root.dataset.booted = '1';
  const cleanups = [bootReveal(root), bootCarousel(root)];
  root._artinkColsCleanup = () => {
    cleanups.forEach((fn) => fn && fn());
    root.dataset.booted = '0';
  };
}

function initAll() {
  document.querySelectorAll('[data-artink-cols]').forEach(bootCollections);
}

initAll();
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-artink-cols]').forEach(bootCollections);
});
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-artink-cols]').forEach((root) => {
    root._artinkColsCleanup?.();
  });
});
